import { useColorScheme } from '@mui/material/styles';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { useCustomerCloudAutoFill } from '../renderers/hooks/useCustomerCloudAutoFill.hooks';
import { useRenderersState } from '../renderers/useRenderersState.hooks';
import { SourceDialogMode } from '../SourceCreate.types';
import useCloudSourceForm from './useCloudSourceForm.hooks';

/**
 * useSourceFormState provides state and handlers for source form components.
 * This hook returns only state, handlers, and flags - no JSX.
 * Components consume this hook's data directly.
 */
const useSourceFormState = () => {
  const { mode: themeMode } = useColorScheme();
  const { reset } = useFormContext();
  const {
    mode,
    selectedOrgData,
    sourceStatus,
    setSourceStatus,
    categoryMasterList,
    categories,
    regions,
    cloudProviders,
    deviceAlertFields,
    handleCategoryChange,
    handleRegionChange,
    handleCloudProviderChange,
    handleDeviceAlertFieldsChange,
    selectedSourceDetails
  } = useCloudSourceForm();

  // Initialize auto-fill hook and remaining renderer hook
  useCustomerCloudAutoFill();
  const renderersState = useRenderersState();

  useEffect(() => {
    if (selectedOrgData?.id) {
      reset({
        org_id: selectedOrgData?.id
      });
    }
  }, [selectedOrgData, reset]);

  useEffect(() => {
    if (mode === SourceDialogMode.New) {
      reset();
    }
  }, [mode, reset]);

  // Return only state, handlers, and flags - no JSX
  return {
    // Theme
    themeMode,

    // Multi-select fields
    categories,
    regions,
    cloudProviders,
    deviceAlertFields,
    categoryMasterList,

    // Handlers
    handleCategoryChange,
    handleRegionChange,
    handleCloudProviderChange,
    handleDeviceAlertFieldsChange,
    setSourceStatus,

    // Source details
    sourceStatus,
    selectedSourceDetails,

    // From useRenderersState hook (also refactored to not return JSX)
    ...renderersState
  };
};

export default useSourceFormState;
